/**
 * Property Relationship Graph Component
 * 
 * Interactive graph visualization showing property relationships.
 * Uses D3.js for rendering.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as d3 from 'd3';
import {
  Card,
  Spin,
  Space,
  Button,
  Select,
  Input,
  Typography,
  Tag,
  message
} from 'antd';
import {
  ReloadOutlined,
  DownloadOutlined,
  SearchOutlined,
  ClearOutlined
} from '@ant-design/icons';
import { PropertyRelationshipService } from '../../services/api/propertyRelationshipService';
import {
  PropertyGraph,
  PropertyGraphNode as NodeType,
  RelationshipType
} from '../../types/propertyRelationships';
import { PropertyGraphNode } from './PropertyGraphNode';
import styles from './PropertyRelationshipGraph.module.css';

const { Option } = Select;
const { Search } = Input;
const { Title, Text } = Typography;

interface PropertyRelationshipGraphProps {
  artifactId: string;
  artifactName: string;
  onPropertySelect?: (propertyId: string) => void;
  filterByRelationshipType?: RelationshipType[];
  height?: number;
}

export const PropertyRelationshipGraph: React.FC<PropertyRelationshipGraphProps> = ({
  artifactId,
  artifactName,
  onPropertySelect,
  filterByRelationshipType,
  height = 600
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [graphData, setGraphData] = useState<PropertyGraph | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedNode, setSelectedNode] = useState<NodeType | null>(null);
  const [searchText, setSearchText] = useState('');
  const [selectedRelationshipTypes, setSelectedRelationshipTypes] = useState<RelationshipType[]>(
    filterByRelationshipType || []
  );
  const [zoom, setZoom] = useState<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);

  // Fetch graph data
  useEffect(() => {
    loadGraphData();
  }, [artifactId]);

  const loadGraphData = async () => {
    setLoading(true);
    try {
      const data = await PropertyRelationshipService.getPropertyGraph(artifactId);
      setGraphData(data);
    } catch (error: any) {
      console.error('Error loading property graph:', error);
      message.error('Failed to load property relationships');
    } finally {
      setLoading(false);
    }
  };

  // Render graph with D3
  useEffect(() => {
    if (!graphData || !svgRef.current || loading) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = containerRef.current?.clientWidth || 800;
    const margin = { top: 20, right: 20, bottom: 20, left: 20 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    // Filter nodes and edges based on search and relationship type filters
    let filteredNodes = graphData.nodes;
    let filteredEdges = graphData.edges;

    if (searchText) {
      filteredNodes = filteredNodes.filter(node =>
        node.propertyKey.toLowerCase().includes(searchText.toLowerCase())
      );
      const nodeIds = new Set(filteredNodes.map(n => n.id));
      filteredEdges = filteredEdges.filter(edge =>
        nodeIds.has(edge.source) && nodeIds.has(edge.target)
      );
    }

    if (selectedRelationshipTypes.length > 0) {
      filteredNodes = filteredNodes.filter(node =>
        selectedRelationshipTypes.includes(node.relationshipType)
      );
      const nodeIds = new Set(filteredNodes.map(n => n.id));
      filteredEdges = filteredEdges.filter(edge =>
        nodeIds.has(edge.source) && nodeIds.has(edge.target)
      );
    }

    // Create force simulation
    const simulation = d3
      .forceSimulation(filteredNodes as any)
      .force(
        'link',
        d3
          .forceLink(filteredEdges)
          .id((d: any) => d.id)
          .distance(100)
      )
      .force('charge', d3.forceManyBody().strength(-300))
      .force('center', d3.forceCenter(innerWidth / 2, innerHeight / 2))
      .force('collision', d3.forceCollide().radius(50));

    // Create container
    const g = svg
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Create zoom behavior
    const zoomBehavior = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform.toString());
      });

    svg.call(zoomBehavior as any);
    setZoom(zoomBehavior);

    // Draw edges
    const links = g
      .append('g')
      .attr('class', 'links')
      .selectAll('line')
      .data(filteredEdges)
      .enter()
      .append('line')
      .attr('stroke', '#999')
      .attr('stroke-opacity', 0.6)
      .attr('stroke-width', 2);

    // Draw nodes as circles (we'll overlay React components)
    const nodeGroups = g
      .append('g')
      .attr('class', 'nodes')
      .selectAll('g')
      .data(filteredNodes)
      .enter()
      .append('g')
      .attr('class', 'node-group')
      .call(
        d3
          .drag<SVGGElement, NodeType>()
          .on('start', dragstarted)
          .on('drag', dragged)
          .on('end', dragended) as any
      );

    // Add circles for nodes
    nodeGroups
      .append('circle')
      .attr('r', 30)
      .attr('fill', (d) => {
        const colors: Record<RelationshipType, string> = {
          [RelationshipType.CALCULATION]: '#1890ff',
          [RelationshipType.RATIO]: '#52c41a',
          [RelationshipType.AGGREGATION]: '#fa8c16',
          [RelationshipType.CONDITION]: '#eb2f96',
          [RelationshipType.SWITCH]: '#722ed1',
          [RelationshipType.INPUT]: '#13c2c2',
          [RelationshipType.DEFAULT]: '#95de64',
          [RelationshipType.GLOBAL_REF]: '#f5222d',
          [RelationshipType.REFERENCED_ARTIFACT]: '#2f54eb',
          [RelationshipType.UNKNOWN]: '#8c8c8c'
        };
        return colors[d.relationshipType] || '#8c8c8c';
      })
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .style('cursor', 'pointer')
      .on('click', (event, d) => {
        setSelectedNode(d);
        onPropertySelect?.(d.propertyId);
      });

    // Add labels
    nodeGroups
      .append('text')
      .text((d) => d.propertyKey)
      .attr('dy', 4)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#262626')
      .style('pointer-events', 'none');

    // Update positions on simulation tick
    simulation.on('tick', () => {
      links
        .attr('x1', (d: any) => d.source.x)
        .attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x)
        .attr('y2', (d: any) => d.target.y);

      nodeGroups.attr('transform', (d: any) => `translate(${d.x},${d.y})`);
    });

    function dragstarted(event: any, d: any) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    function dragged(event: any, d: any) {
      d.fx = event.x;
      d.fy = event.y;
    }

    function dragended(event: any, d: any) {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }

    // Cleanup
    return () => {
      simulation.stop();
    };
  }, [graphData, searchText, selectedRelationshipTypes, height, loading, onPropertySelect]);

  const handleExport = () => {
    if (!svgRef.current) return;
    const svgData = new XMLSerializer().serializeToString(svgRef.current);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);
    const downloadLink = document.createElement('a');
    downloadLink.href = svgUrl;
    downloadLink.download = `property-graph-${artifactId}.svg`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const handleClearFilters = () => {
    setSearchText('');
    setSelectedRelationshipTypes([]);
  };

  return (
    <Card
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>
            Property Relationships: {artifactName}
          </Title>
          {graphData?.relationshipSummary && (
            <Space>
              <Tag>Calculations: {graphData.relationshipSummary.calculations}</Tag>
              <Tag>Ratios: {graphData.relationshipSummary.ratios}</Tag>
              <Tag>Aggregations: {graphData.relationshipSummary.aggregations}</Tag>
              <Tag>Conditions: {graphData.relationshipSummary.conditions}</Tag>
            </Space>
          )}
        </Space>
      }
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadGraphData} loading={loading}>
            Refresh
          </Button>
          <Button icon={<DownloadOutlined />} onClick={handleExport}>
            Export
          </Button>
        </Space>
      }
    >
      <div className={styles.propertyGraphContainer}>
        <div className={styles.propertyGraphFilters}>
          <Search
            placeholder="Search properties..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <Select
            mode="multiple"
            placeholder="Filter by relationship type"
            value={selectedRelationshipTypes}
            onChange={setSelectedRelationshipTypes}
            style={{ width: 300 }}
            allowClear
          >
            <Option value={RelationshipType.CALCULATION}>Calculation</Option>
            <Option value={RelationshipType.RATIO}>Ratio</Option>
            <Option value={RelationshipType.AGGREGATION}>Aggregation</Option>
            <Option value={RelationshipType.CONDITION}>Condition</Option>
            <Option value={RelationshipType.SWITCH}>Switch</Option>
            <Option value={RelationshipType.INPUT}>Input</Option>
            <Option value={RelationshipType.DEFAULT}>Default</Option>
            <Option value={RelationshipType.GLOBAL_REF}>Global Variable</Option>
          </Select>
          {(searchText || selectedRelationshipTypes.length > 0) && (
            <Button icon={<ClearOutlined />} onClick={handleClearFilters}>
              Clear Filters
            </Button>
          )}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '100px 0' }}>
            <Spin size="large" />
          </div>
        ) : (
          <div ref={containerRef} className={styles.propertyGraphSvgContainer}>
            <svg ref={svgRef} className={styles.propertyGraphSvg} />
            {selectedNode && (
              <div className={styles.propertyGraphSelectedInfo}>
                <Text strong>Selected: {selectedNode.propertyKey}</Text>
                <br />
                <Text type="secondary">Type: {selectedNode.propertyType}</Text>
                <br />
                <Text type="secondary">
                  Relationship: {selectedNode.relationshipType}
                </Text>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

